import * as React from "react";
import type {Markup3D} from "../../logic3d/elements3d/markups/abstract/Markup3D";
import type {SpaceViewRenderer} from "../../logic3d/renderers/SpaceViewRenderer";
import type {Markup} from "../../../../../../data/models/Markup";
import {KeyboardListener} from "../../../../../../utils/interaction/key/KeyboardListener";
import {MarkupType} from "../../../../../../generated/api/base";
import {MarkupTextInputElement} from "./MarkupTextInputElement";
import {MarkupTextActionBar} from "./MarkupTextActionBar";

interface IMarkupTextModifierProps {
	readonly style: React.CSSProperties;
	readonly barRef: React.Ref<HTMLDivElement>;
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly markup3D: Markup3D;
	readonly onClose: () => void;
}

export class MarkupTextModifier extends React.Component<IMarkupTextModifierProps> {
	private _inputRef = React.createRef<MarkupTextInputElement>();
	private _stringifiedTextObjectOnMount: string;

	private onKeyUp = (event: KeyboardEvent) => {
		switch (event.key) {
			case KeyboardListener.KEY_ESCAPE:
				this.props.onClose();
				break;
		}
	};

	private exitTextMode(markup3D: Markup3D) {
		const {spaceViewRenderer} = this.props;
		let content = this._inputRef.current?.innerText ?? "";

		while (content.endsWith("\n")) {
			content = content.slice(0, -1);
		}
		(markup3D.modelData as Markup).text.content = content;
		markup3D.isTextInHtmlEditMode = false;

		const doesContainOnlyWhiteSpace = markup3D.textContent.trim().length === 0;

		if (markup3D.type === MarkupType.TextBox && doesContainOnlyWhiteSpace) {
			markup3D.destroy(true);
			spaceViewRenderer.spaceItemController.updateActionBar();
		} else {
			const newStringifiedTextObjectOnMount = JSON.stringify((markup3D?.modelData as Markup)?.text || {});

			if (this._stringifiedTextObjectOnMount !== newStringifiedTextObjectOnMount) {
				spaceViewRenderer.markupManager.updateItems([markup3D], true);
				this._stringifiedTextObjectOnMount = newStringifiedTextObjectOnMount;
			}
		}
		spaceViewRenderer.spaceItemController.markupTextManager.recreateGeometry();
		spaceViewRenderer.needsRender = true;
	}

	public override componentDidMount() {
		this.props.markup3D.isTextInHtmlEditMode = true;
		KeyboardListener.getInstance().signals.up.add(this.onKeyUp);

		this._stringifiedTextObjectOnMount = JSON.stringify((this.props.markup3D?.modelData as Markup)?.text || {});
	}

	public override UNSAFE_componentWillUpdate(nextProps: Readonly<IMarkupTextModifierProps>, nextState: Readonly<{}>, nextContext: any): void {
		if (nextProps.markup3D !== this.props.markup3D) {
			this.exitTextMode(this.props.markup3D);
			this.props.onClose();
		}
	}

	public override componentWillUnmount() {
		this.props.markup3D.isTextInHtmlEditMode = false;
		KeyboardListener.getInstance().signals.up.remove(this.onKeyUp);

		const {markup3D} = this.props;

		this.exitTextMode(markup3D);
		this.props.onClose();
	}

	public override render() {
		return (
			<>
				<MarkupTextInputElement
					ref={this._inputRef}
					spaceViewRenderer={this.props.spaceViewRenderer}
					markup3D={this.props.markup3D}
				/>
				<div
					ref={this.props.barRef}
					className="textStyleModifierContainer"
					style={this.props.style}
				>
					<MarkupTextActionBar
						inputElementRef={this._inputRef}
						markup3D={this.props.markup3D}
						onClose={this.props.onClose}
					/>
				</div>
			</>
		);
	}
}
